import { useState } from 'react'
import { toNumber } from 'lodash'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { Label, ParagraphMedium } from '@/components/text'
import useMediaQuery from '@/hooks/use-media-query'
import { colors, breakpoints, fontWeights } from '@/styles'
import { Arrow } from '@/components/animations'

const Wrapper = styled.button`
  overflow: hidden;
  width: ${props => props.width || '256px'};
  min-height: 140px;
  border-radius: 6px;
  max-height: 197px;
  min-width: 250px;
  flex: 1;
  background: ${props =>
    props.id === 'listing-card-application-sponsor-listing-sponsored-listing-tab-click'
      ? colors.blue96
      : colors.white};
  border: 1px solid ${props =>
    props.id === 'listing-card-application-sponsor-listing-sponsored-listing-tab-click'
      ? colors.blue88
      : colors.grey88}};
  box-shadow: 0px 4px 6px -2px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: background-color 300ms ease-in-out 0s, box-shadow 300ms ease-in-out 0s;
  &:not(:disabled) {
    &:hover,
    &:focus {
      box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
    }
  }

  &:disabled {
    user-select: none;
    box-shadow: none;
    border: 1px solid ${colors.grey96};
    cursor: not-allowed;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: unset;
    width: 100%;
    &:not(:disabled) {
      &:active {
        background-color: ${colors.grey96};
      }
    }
  }
`

const Content = styled.div`
  height: 100%;
  text-align: left;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  // prevent child elements from registering as click target in GTM
  pointer-events: none;

  ${props =>
    props.$disabled &&
    `
    opacity: 0.4;
  `}

  @media screen and (max-width: 767px) {
    flex-direction: row;
    align-items: center;
  }
`

const IconWrapper = styled.div`
  position: relative;
  width: 20px;
  color: ${colors.regular};
  > svg {
    flex-shrink: 0;
  }
`

const Balloon = styled.span`
  position: absolute;
  right: -8px;
  top: -8px;
  background-color: ${colors.primary};
  border-radius: 15px;
  height: 19px;
  min-width: 19px;
  line-height: 19px;
  text-align: center;
  color: ${colors.white};
  font-size: 12px;
  font-weight: ${fontWeights.medium};
  padding: 0 4px;
`

const CardTitle = styled.div`
  width: 100%;
  color: ${colors.regular};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0 8px;
  font-weight: 600;
  @media screen and (max-width: 767px) {
    justify-content: space-between;
    margin: 0;
  }
`

const Description = styled.span`
  color: ${colors.grey24};
  margin: 0;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
`

const truncateNumber = n => (n > 99 ? '99+' : n.toString())

const MenuCard = ({ item, id, width, className }) => {
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const smallestScreen = useMediaQuery(`(max-width: 767px)`)
  const [active, setActive] = useState(false)
  if (!item) return null
  const CardComponent = item.onClick || item.disabled ? undefined : Link
  const enter = () => setActive(true)
  const leave = () => setActive(false)

  const handlers = !item.disabled
    ? {
        onMouseLeave: leave,
        onMouseEnter: enter,
        onMouseDown: enter,
        onMouseUp: leave,
        onFocus: enter,
        onBlur: leave
      }
    : {}
  const Icon = item.icon
  return (
    <Wrapper
      as={CardComponent}
      to={!item.disabled ? item.to : undefined}
      onClick={item.onClick}
      disabled={item.disabled}
      {...handlers}
      id={id}
      width={width}
      className={className}
    >
      <Content $disabled={item.disabled}>
        <IconWrapper>
          <Icon />
          {toNumber(item.count) > 0 && <Balloon>{truncateNumber(item.count)}</Balloon>}
        </IconWrapper>
        <CardTitle>
          <Label as="span" type="large">
            {displayMobile ? item.mobileTitle || item.title : item.title}
          </Label>
          <Arrow isOpen={!displayMobile && active} align={item.disabled ? 'top' : 'baseline'} />
        </CardTitle>

        {!smallestScreen && (
          <Description>
            <ParagraphMedium as="span">{item.description}</ParagraphMedium>
          </Description>
        )}
      </Content>
    </Wrapper>
  )
}
export default MenuCard
